// Adding / removing ONE regular broadcast to / from several composite broadcasts at once, worked
// out as a set of multi-path updates (the caller writes them with `updateMany`, ONE atomic
// write). Nothing here touches Firebase.

import { planBroadcastUpdates } from "lib/broadcast"
import { isCompositeBroadcast } from "lib/playlist"
import { FIELDS, sameField, stored } from "lib/playlistChange"

// Adds the broadcast to the END of some composite broadcasts and removes it (every time it
// appears) from others. Work it out from fresh data (`getAll`), not from cached lists.
//  - broadcasts / contentItems: all of the client's, each with its `id` (multiplier copies too)
//  - broadcastId: the regular broadcast (not a copy, not a composite one)
//  - add / remove: ids of composite broadcasts. One that already plays the broadcast is not given
//    it again, one that does not play it is left alone.
// A composite broadcast may list a multiplier copy of the broadcast instead of the broadcast
// itself. A copy stands for the same broadcast, so it counts as "playing it": it is not added
// next to it, and removing takes the copies out too.
// Everything that depends on the changed composites follows (the same code as when a broadcast
// is saved): the total duration, and the composites built on them. Only fields that really
// changed are written, and `updated_at` moves only on the broadcasts that changed.
// Returns { updates, added, removed }: `added` / `removed` are the ids of the composite
// broadcasts that really changed.
export const planCompositeMembership = ({ broadcasts, contentItems, broadcastId, add = [], remove = [], now = new Date().toISOString() }) => {
  const working = new Map(broadcasts.map(b => [b.id, { ...b }]))
  const target = working.get(broadcastId)
  if (!target || target.sourceId || isCompositeBroadcast(target)) {
    return { updates: {}, added: [], removed: [] }
  }

  const family = new Set([broadcastId, ...broadcasts.filter(b => b.sourceId === broadcastId).map(b => b.id)])
  const playsIt = (record) => (record.broadcastList || []).some(id => family.has(id))
  const composite = (id) => {
    const record = working.get(id)
    return record && !record.sourceId && isCompositeBroadcast(record) ? record : null
  }

  const added = []
  const removed = []

  // Saves a new list for one composite broadcast, and the effects of that on the others
  const change = (id, broadcastList) => {
    const edited = { ...working.get(id), broadcastList, updated_at: now }
    const plan = planBroadcastUpdates({ broadcasts: [...working.values()], contentItems, edited, now })
    working.set(id, plan.edited)
    for (const [path, value] of Object.entries(plan.updates)) {
      const [, otherId, field] = path.split("/")
      const other = working.get(otherId)
      if (!other) continue
      if (value === null) delete other[field]
      else other[field] = value
    }
  }

  for (const id of new Set(remove)) {
    const record = composite(id)
    if (record && playsIt(record)) {
      change(id, record.broadcastList.filter(item => !family.has(item)))
      removed.push(id)
    }
  }
  for (const id of new Set(add)) {
    const record = composite(id)
    if (record && !playsIt(record)) {
      change(id, [...(record.broadcastList || []), broadcastId])
      added.push(id)
    }
  }

  // What differs from the data that came in, field by field
  const updates = {}
  for (const original of broadcasts) {
    const next = working.get(original.id)
    for (const field of FIELDS) {
      if (!sameField(field, original[field], next[field])) {
        updates[`broadcast/${original.id}/${field}`] = stored(field, next[field])
      }
    }
  }
  return { updates, added, removed }
}
